import { supabase } from "@/integrations/supabase/client";
import { RawSessionData } from "@/types/mentorSessions";

export async function getMentorId(profileId: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from("mentor_info")
      .select("id")
      .eq("profile_id", profileId)
      .single();
      
    if (error) {
      console.error("Error fetching mentor info:", error);
      throw error;
    }

    return data?.id || null;
  } catch (error) {
    console.error("Error in getMentorId:", error);
    throw error;
  }
}

export async function getMentorSessions(mentorId: string): Promise<RawSessionData[]> {
  try {
    console.log('🔍 [getMentorSessions] Buscando sessões para mentor ID:', mentorId);
    
    // Primeiro, tentar buscar pelo mentor_id (que referencia mentor_info.id)
    console.log('🔍 [getMentorSessions] Executando query com mentor_id:', mentorId);
    
    const { data: mentorSessions, error: mentorError } = await supabase
      .from("sessions")
      .select(`
        id,
        scheduled_date,
        scheduled_start,
        scheduled_end,
        status,
        payment_released,
        game_id,
        player_profile_id,
        is_group,
        meeting_link,
        created_at,
        offering_id,
        mentor_id,
        games(name),
        mentorship_offerings!offering_id(title, cover_image_url, price_cents),
        payments(id, amount_cents, status, stripe_payment_id, created_at)
      `)
      .eq("mentor_id", mentorId)
      .order("created_at", { ascending: false });

    console.log('📊 [getMentorSessions] Resultado da query:', {
      hasData: !!mentorSessions,
      dataLength: mentorSessions?.length || 0,
      hasError: !!mentorError,
      error: mentorError ? {
        message: mentorError.message,
        code: mentorError.code,
        details: mentorError.details,
        hint: mentorError.hint
      } : null
    });

    if (mentorError) {
      console.error('❌ [getMentorSessions] Erro na query por mentor_id:', mentorError);
      console.error('❌ [getMentorSessions] Detalhes do erro:', JSON.stringify(mentorError, null, 2));
      throw mentorError;
    }

    // Se não encontrou sessões, verificar se existem sessões com outros mentor_ids para debug
    if (!mentorSessions || mentorSessions.length === 0) {
      console.log('⚠️ [getMentorSessions] Nenhuma sessão encontrada para mentor_id:', mentorId);
      
      // Debug: Verificar se existem sessões recentes para entender o problema
      const { data: recentSessions, error: debugError } = await supabase
        .from("sessions")
        .select("id, mentor_id, status, created_at, player_profile_id")
        .order("created_at", { ascending: false })
        .limit(10);
      
      if (!debugError && recentSessions) {
        console.log('🔍 [getMentorSessions] DEBUG - Últimas 10 sessões criadas:', recentSessions.map(s => ({
          id: s.id,
          mentor_id: s.mentor_id,
          status: s.status,
          created_at: s.created_at,
          player_profile_id: s.player_profile_id
        })));
        console.log('🔍 [getMentorSessions] DEBUG - Mentor ID esperado:', mentorId);
        console.log('🔍 [getMentorSessions] DEBUG - Comparação:', {
          esperado: mentorId,
          encontrados: recentSessions.map(s => s.mentor_id),
          match: recentSessions.some(s => s.mentor_id === mentorId)
        });
      }
      
      return [];
    }

    const data = mentorSessions || [];
    console.log('✅ [getMentorSessions] Dados encontrados via mentor_id:', {
      count: data.length,
      sessions: data.map(s => ({
        id: s.id,
        status: s.status,
        mentor_id: s.mentor_id,
        meeting_link: s.meeting_link || 'VAZIO',
        hasMeetingLink: !!(s.meeting_link && s.meeting_link.trim() !== ''),
        hasPayments: !!(s.payments && s.payments.length > 0),
        paymentsCount: s.payments?.length || 0,
        created_at: s.created_at
      }))
    });

    return data;
  } catch (error) {
    console.error('💥 [getMentorSessions] Erro:', error);
    throw error;
  }
}

export async function getPlayerProfiles(playerIds: string[]) {
  if (!playerIds.length) return new Map();
  
  try {
    const uniquePlayerIds = [...new Set(playerIds)];
    const playerProfilesMap = new Map();

    // OTIMIZAÇÃO: Buscar tudo em batch de uma vez (muito mais rápido)
    // 1. Buscar perfis em batch
    const { data: profilesData, error: profilesError } = await supabase
      .from("profiles")
      .select("id, nickname, preferred_game, role")
      .in("id", uniquePlayerIds);

    if (profilesError) {
      console.error("❌ [getPlayerProfiles] Erro ao buscar perfis:", profilesError);
    }

    // 2. Buscar imagens em batch
    const { data: imagesData, error: imagesError } = await supabase
      .from("player_social_media")
      .select("profile_id, image_url")
      .in("profile_id", uniquePlayerIds)
      .not("image_url", "is", null);

    if (imagesError) {
      console.warn("⚠️ [getPlayerProfiles] Erro ao buscar imagens:", imagesError);
    }

    // Criar mapa de imagens
    const imagesMap = new Map();
    (imagesData || []).forEach(img => {
      if (img.image_url) {
        imagesMap.set(img.profile_id, img.image_url);
      }
    });

    // Processar perfis encontrados
    const processedIds = new Set();
    (profilesData || []).forEach(profile => {
      const playerNickname = profile.nickname?.trim() || `Jogador-${profile.id.substring(0, 8)}`;
      const playerImageUrl = imagesMap.get(profile.id) || null;
      
      playerProfilesMap.set(profile.id, {
        nickname: playerNickname,
        image_url: playerImageUrl
      });
      processedIds.add(profile.id);
    });

    // Fallback para IDs não encontrados
    uniquePlayerIds.forEach(playerId => {
      if (!processedIds.has(playerId)) {
        const shortId = playerId.substring(0, 8);
        const playerImageUrl = imagesMap.get(playerId) || null;
        playerProfilesMap.set(playerId, {
          nickname: `Jogador-${shortId}`,
          image_url: playerImageUrl
        });
      }
    });
    
    // Log apenas em desenvolvimento
    if (import.meta.env.DEV) {
      console.log(`📊 [getPlayerProfiles] ${processedIds.size}/${uniquePlayerIds.length} perfis encontrados`);
    }
    
    return playerProfilesMap;
  } catch (error) {
    console.error("❌ [getPlayerProfiles] Erro crítico:", error);
    
    // Fallback de emergência
    const fallbackMap = new Map();
    [...new Set(playerIds)].forEach(playerId => {
      const shortId = playerId.substring(0, 8);
      fallbackMap.set(playerId, {
        nickname: `Jogador-${shortId}`,
        image_url: null
      });
    });
    
    return fallbackMap;
  }
}

export async function verifyPayment(sessionId: string, stripeSessionId: string) {
  try {
    const { data, error } = await supabase.functions.invoke("verify-payment", {
      body: { 
        sessionId: sessionId,
        stripeSessionId: stripeSessionId
      }
    });
    
    if (error) {
      console.error(`Error verifying payment for session ${sessionId}:`, error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error(`Error during payment verification for session ${sessionId}:`, error);
    throw error;
  }
}

export async function updateSessionMeetingLink(sessionId: string, meetingLink: string): Promise<void> {
  try {
    console.log(`🔗 [updateSessionMeetingLink] Iniciando para sessão ${sessionId}`);
    console.log(`🔗 [updateSessionMeetingLink] Link recebido: "${meetingLink}"`);
    console.log(`🔗 [updateSessionMeetingLink] Link length: ${meetingLink?.length || 0}`);
    
    // Validar que o link não está vazio
    const trimmedLink = meetingLink?.trim() || '';
    if (!trimmedLink) {
      console.error(`❌ [updateSessionMeetingLink] Link vazio recebido para sessão ${sessionId}`);
      throw new Error('Link não pode estar vazio');
    }
    
    // First get session information before update
    const { data: sessionData, error: fetchError } = await supabase
      .from("sessions")
      .select(`
        player_profile_id, 
        mentor_id,
        scheduled_date,
        scheduled_start,
        scheduled_end,
        games(name),
        mentor_info(profiles(nickname)),
        profiles!player_profile_id(email, nickname)
      `)
      .eq("id", sessionId)
      .single();
      
    console.log(`📊 [updateSessionMeetingLink] Dados da sessão:`, sessionData);
    
    if (fetchError) {
      console.error(`❌ [updateSessionMeetingLink] Erro ao buscar dados da sessão ${sessionId}:`, fetchError);
      throw fetchError;
    }
    
    if (!sessionData) {
      console.error(`❌ [updateSessionMeetingLink] Nenhum dado encontrado para sessão ${sessionId}`);
      throw new Error('Sessão não encontrada');
    }
    
    // Update the meeting link (usar trimmedLink para garantir que não há espaços)
    console.log(`💾 [updateSessionMeetingLink] Salvando link no banco: "${trimmedLink}"`);
    
    // Primeiro fazer o update sem .single() para evitar erro se RLS bloquear
    // Usar .select() para verificar quantas linhas foram afetadas
    const { data: updateResult, error: updateError, count } = await (supabase
      .from("sessions")
      .update({ meeting_link: trimmedLink })
      .eq("id", sessionId) as any)
      .select('id', { count: 'exact' });
    
    console.log(`📊 [updateSessionMeetingLink] Resultado do UPDATE:`, {
      hasError: !!updateError,
      error: updateError,
      rowsAffected: updateResult?.length || 0,
      count: count
    });
    
    if (updateError) {
      console.error(`❌ [updateSessionMeetingLink] Erro ao atualizar link para sessão ${sessionId}:`, updateError);
      console.error(`❌ [updateSessionMeetingLink] Detalhes do erro:`, {
        code: updateError.code,
        message: updateError.message,
        details: updateError.details,
        hint: updateError.hint
      });
      
      // Mensagem mais amigável para o usuário
      if (updateError.code === 'PGRST301' || updateError.message?.includes('permission denied')) {
        throw new Error('Você não tem permissão para atualizar esta sessão. Verifique se você é o mentor responsável.');
      } else if (updateError.code === 'PGRST116') {
        throw new Error('Não foi possível atualizar o link. A sessão pode não existir ou você não tem permissão.');
      } else {
        throw new Error(`Erro ao salvar link: ${updateError.message || 'Erro desconhecido'}`);
      }
    }
    
    // Verificar se alguma linha foi atualizada
    if (!updateResult || updateResult.length === 0) {
      console.warn(`⚠️ [updateSessionMeetingLink] Nenhuma linha foi atualizada! Isso pode indicar problema de RLS ou sessão não encontrada.`);
      throw new Error('Não foi possível atualizar o link. Verifique se você tem permissão para editar esta sessão.');
    }
    
    console.log(`✅ [updateSessionMeetingLink] UPDATE executado com sucesso! ${updateResult.length} linha(s) afetada(s)`);
    
    // Verificar se o update foi bem-sucedido fazendo um SELECT separado
    // Usar service_role para garantir que podemos ler o valor mesmo com RLS
    const { data: updatedData, error: selectError } = await supabase
      .from("sessions")
      .select('meeting_link')
      .eq("id", sessionId)
      .maybeSingle(); // Usar maybeSingle() em vez de single() para não falhar se não encontrar
    
    if (selectError) {
      console.error(`⚠️ [updateSessionMeetingLink] Erro ao verificar link atualizado:`, selectError);
      // Não falhar aqui - o update pode ter funcionado mesmo se o select falhar por RLS
      // Assumir que o update foi bem-sucedido se não houve erro no UPDATE
      console.log(`✅ [updateSessionMeetingLink] Link atualizado (não foi possível verificar devido a RLS, mas assumindo sucesso)`);
    } else if (updatedData) {
      console.log(`✅ [updateSessionMeetingLink] Link atualizado com sucesso!`);
      console.log(`✅ [updateSessionMeetingLink] Link salvo no banco: "${updatedData.meeting_link || 'null'}"`);
    } else {
      // Se não retornou dados mas também não houve erro, assumir sucesso
      console.log(`✅ [updateSessionMeetingLink] Link atualizado (assumindo sucesso - dados não retornados por RLS)`);
    }
    
    // Check player data
    console.log(`👤 [updateSessionMeetingLink] Dados do jogador:`, {
      player_profile_id: sessionData.player_profile_id,
      player_email: sessionData.profiles?.email,
      player_nickname: sessionData.profiles?.nickname
    });
    
    // Send email notification to player about the meeting link
    if (sessionData && sessionData.profiles && sessionData.profiles.email) {
      try {
        const playerEmail = sessionData.profiles.email;
        const mentorName = sessionData.mentor_info?.profiles?.nickname || "Seu mentor";
        const mentorshipTitle = `Mentoria de ${sessionData.games?.name || ""}`;
        const scheduledDate = sessionData.scheduled_date;
        const scheduledTime = `${sessionData.scheduled_start.substring(0, 5)} - ${sessionData.scheduled_end.substring(0, 5)}`;
        
        console.log(`📧 [updateSessionMeetingLink] Preparando email para:`, {
          to: playerEmail,
          mentorName,
          mentorshipTitle,
          scheduledDate,
          scheduledTime,
          meetingLink
        });
        
        const emailResult = await supabase.functions.invoke("send-email", {
          body: {
            to: playerEmail,
            template: "meetingLinkAdded",
            data: {
              mentorshipTitle,
              mentorName,
              scheduledDate,
              scheduledTime,
              meetingLink,
              appUrl: window.location.origin
            }
          }
        });
        
        if (emailResult.error) {
          console.error(`❌ [updateSessionMeetingLink] Erro na função send-email:`, emailResult.error);
          throw emailResult.error;
        }
        
        console.log(`✅ [updateSessionMeetingLink] Email enviado com sucesso para ${playerEmail}`);
        console.log(`📧 [updateSessionMeetingLink] ID do email:`, emailResult.data?.id);
        
      } catch (emailError) {
        console.error(`❌ [updateSessionMeetingLink] Erro ao enviar email:`, emailError);
        // Log detailed error information
        if (emailError.message) {
          console.error(`❌ [updateSessionMeetingLink] Mensagem do erro:`, emailError.message);
        }
        if (emailError.details) {
          console.error(`❌ [updateSessionMeetingLink] Detalhes do erro:`, emailError.details);
        }
        // Continue execution even if email fails - the link update was successful
        console.log(`⚠️ [updateSessionMeetingLink] Link foi salvo, mas email falhou. Continuando...`);
      }
    } else {
      console.warn(`⚠️ [updateSessionMeetingLink] Dados do jogador incompletos para envio de email:`, {
        hasSessionData: !!sessionData,
        hasProfiles: !!sessionData?.profiles,
        hasEmail: !!sessionData?.profiles?.email,
        email: sessionData?.profiles?.email || 'N/A'
      });
    }
  } catch (error) {
    console.error(`❌ [updateSessionMeetingLink] Erro geral na função para sessão ${sessionId}:`, error);
    throw error;
  }
}

export async function debugSecurityCheck(profileId: string) {
  console.log('🔒 [SECURITY DEBUG] Iniciando verificação de segurança para profile:', profileId);
  
  try {
    // 1. Verificar qual mentor_id corresponde a este profile
    const mentorId = await getMentorId(profileId);
    console.log('🔒 [SECURITY DEBUG] Mentor ID encontrado:', mentorId);
    
    if (!mentorId) {
      console.log('🔒 [SECURITY DEBUG] Este profile não é um mentor');
      return;
    }
    
    // 2. Buscar sessões usando o mentor_id
    const sessions = await getMentorSessions(mentorId);
    console.log('🔒 [SECURITY DEBUG] Sessões encontradas:', sessions.length);
    
    // 3. Verificar se todas as sessões têm o mentor_id correto
    const invalidSessions = [];
    for (const session of sessions) {
      // Para verificar, vamos buscar a sessão diretamente e confirmar o mentor_id
      const { data: sessionCheck } = await supabase
        .from('sessions')
        .select('id, mentor_id')
        .eq('id', session.id)
        .single();
        
      if (sessionCheck && sessionCheck.mentor_id !== mentorId) {
        invalidSessions.push({
          sessionId: session.id,
          expectedMentorId: mentorId,
          actualMentorId: sessionCheck.mentor_id
        });
      }
    }
    
    if (invalidSessions.length > 0) {
      console.error('🚨 [SECURITY BREACH] Sessões de outros mentores encontradas:', invalidSessions);
    } else {
      console.log('✅ [SECURITY CHECK] Todas as sessões pertencem ao mentor correto');
    }
    
    // 4. Verificar se existem ofertas que pertencem a outros mentores
    const { data: mentorOfferings } = await supabase
      .from('mentorship_offerings')
      .select('id, mentor_id')
      .eq('mentor_id', mentorId);
      
    console.log('🔒 [SECURITY DEBUG] Ofertas do mentor:', mentorOfferings?.length || 0);
    
    return {
      profileId,
      mentorId,
      sessionsCount: sessions.length,
      invalidSessions,
      isSecure: invalidSessions.length === 0
    };
    
  } catch (error) {
    console.error('🔒 [SECURITY DEBUG] Erro durante verificação:', error);
    throw error;
  }
}
